import { useEffect, useMemo, useRef, useState } from 'react';
import qrcode from 'qrcode-generator';
import { pairUrl } from '../../store/usePairing.js';

// Connect an iPad.
//
// The advisor scans this with the iPad's camera and the same assessment opens
// there, for the same household, joined to this screen (Jimmy, 2026-10-03).
// The code carries an address and nothing else: the iPad still signs in the
// way any device does before it is shown anything.
//
// On a developer's machine the page is usually open at "localhost", which an
// iPad cannot reach, so the dev server is asked for its address on the local
// network and that goes in the code instead. If it has none (it was started
// without --host), the dialog says so rather than showing a code that will
// not open.

const LOCAL = /^(localhost|127\.0\.0\.1|\[::1\])$/;

/** The dark squares of a QR code for `text`, as one SVG path. */
export function qrPath(text) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    }
  }
  return { size: n, d };
}

function QrCode({ text }) {
  const { size, d } = useMemo(() => qrPath(text), [text]);
  const quiet = 4;
  const box = size + quiet * 2;
  return (
    <svg className="conv-qr" viewBox={`${-quiet} ${-quiet} ${box} ${box}`} role="img" aria-label="Code to scan with the iPad" shapeRendering="crispEdges">
      <rect x={-quiet} y={-quiet} width={box} height={box} fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  );
}

export default function PairDialog({ open, onClose, code, leadId, connected, available }) {
  const ref = useRef(null);
  const [origin, setOrigin] = useState(() => (typeof window === 'undefined' ? '' : window.location.origin));
  const [unreachable, setUnreachable] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // A page open at localhost asks the dev server where an iPad can find it.
  useEffect(() => {
    if (!open || !available || !LOCAL.test(window.location.hostname)) return undefined;
    let live = true;
    fetch('/__live/address')
      .then(r => (r.ok ? r.json() : { network: [] }))
      .then(body => {
        if (!live) return;
        const first = Array.isArray(body.network) ? body.network[0] : '';
        if (first) {
          setOrigin(first.replace(/\/$/, ''));
          setUnreachable(false);
        } else {
          setUnreachable(true);
        }
      })
      .catch(() => { if (live) setUnreachable(true); });
    return () => { live = false; };
  }, [open, available]);

  const slides = new URLSearchParams(window.location.search).get('slides') === '1';
  const url = code ? pairUrl({ origin, pathname: window.location.pathname, leadId, code, slides }) : '';

  return (
    <dialog className="conv-pair" ref={ref} aria-label="Connect an iPad" onCancel={e => { e.preventDefault(); onClose(); }}>
      <h3>Connect an iPad</h3>
      {!available ? (
        <p>
          This site cannot link two devices yet. It needs a real-time service that has not been set up here, so for
          now this works on the local preview only.
        </p>
      ) : (
        <>
          <p>
            Point the iPad&rsquo;s camera at this code. The same assessment opens there, and what you draw on the iPad
            shows on this screen as you draw it.
          </p>
          {url ? <QrCode text={url} /> : null}
          <p className="conv-pair-link"><a href={url}>{url}</a></p>
          {unreachable ? (
            <p className="conv-pair-note">
              This preview is open at an address only this computer can reach. Start it with the network option and
              the code will point at an address the iPad can open.
            </p>
          ) : null}
          <p className={`conv-pair-status${connected ? ' is-on' : ''}`} role="status" aria-live="polite">
            <span aria-hidden="true" />
            {connected ? 'iPad connected' : 'Waiting for the iPad'}
          </p>
        </>
      )}
      <div className="conv-actions" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="conv-btn is-primary" onClick={onClose}>Done</button>
      </div>
    </dialog>
  );
}
